/**
 * The meaning → {glyph, label, definition} registry behind the Actions-column
 * legend (ActionsHeaderHelp) — the user-facing companion to icons.ts, which
 * maps the same meanings to glyphs for engineers.
 *
 * One definition per meaning, defined once: "Duplicate" reads identically on
 * every surface that offers it. Keys are meanings, not glyphs — Eye appears
 * under both `view` and `preview` because those are different promises to the
 * user, and Trash2 under both `delete` (destroys the record) and `remove` /
 * `stop-tracking` (unlinks it, record survives).
 *
 * Adding a key: import the glyph from icons.ts when a semantic name exists
 * there; domain glyphs without one (Vote, Flame, Star, …) import lucide
 * directly, per the icons convention. Definitions follow the help style guide:
 * what happens when you click, present tense, one sentence; destructive
 * actions say so. Every label must also appear in the Help Center's
 * icon-glossary article — actionsHelpConventions.test.ts enforces that.
 *
 * PRD: Documentation/ACTION_ICONS_HELP_PRD.md
 */
import type { LucideIcon } from 'lucide-react';
import {
  Ban,
  CalendarPlus,
  Flame,
  FlaskConical,
  Link2,
  Megaphone,
  MessageSquare,
  Star,
  Vote,
} from 'lucide-react';
import {
  AnalyticsIcon,
  CloseIcon,
  DeleteIcon,
  DownloadIcon,
  DuplicateIcon,
  EditIcon,
  EmbedIcon,
  OpenExternalIcon,
  PeopleIcon,
  PreviewIcon,
  RunIcon,
  SendIcon,
  SettingsIcon,
  ShareIcon,
  SuccessIcon,
  TrackIcon,
} from './icons';

export interface ActionIconGlossaryEntry {
  /** The ratified glyph — the exact component the buttons render. */
  Icon: LucideIcon;
  /** Short name; also the source for the buttons' title/aria-label. */
  label: string;
  /** One sentence: what happens when you click it. */
  definition: string;
}

export const ACTION_ICON_GLOSSARY = {
  // --- Viewing ---
  view: {
    Icon: PreviewIcon,
    label: 'View',
    definition: 'Opens the full details of this record.',
  },
  preview: {
    Icon: PreviewIcon,
    label: 'Preview',
    definition: 'Shows the finished output in place, without leaving this page.',
  },
  'open-public-page': {
    Icon: PreviewIcon,
    label: 'Open live page',
    definition: 'Opens the live public page in a new browser tab.',
  },
  'open-external': {
    Icon: OpenExternalIcon,
    label: 'Open link',
    definition: 'Opens the linked address in a new browser tab.',
  },

  // --- Editing & lifecycle ---
  edit: {
    Icon: EditIcon,
    label: 'Edit',
    definition: 'Opens this record for editing.',
  },
  duplicate: {
    Icon: DuplicateIcon,
    label: 'Duplicate',
    definition: 'Creates an editable copy of this record.',
  },
  delete: {
    Icon: DeleteIcon,
    label: 'Delete',
    definition: "Permanently deletes this record for everyone — it can't be undone.",
  },
  remove: {
    Icon: DeleteIcon,
    label: 'Remove',
    definition: 'Removes it from this list; the underlying record is not deleted.',
  },
  'stop-tracking': {
    Icon: DeleteIcon,
    label: 'Stop tracking',
    definition: 'Removes it from this workspace; the record itself is unaffected.',
  },
  'cancel-invite': {
    Icon: CloseIcon,
    label: 'Cancel invitation',
    definition: 'Withdraws a pending invitation before it is accepted.',
  },

  // --- Sharing & sending ---
  share: {
    Icon: ShareIcon,
    label: 'Share',
    definition: 'Opens the sharing options for this record.',
  },
  'public-link': {
    Icon: ShareIcon,
    label: 'Public link',
    definition: 'Copies a public link that anyone can open, sign-in not required.',
  },
  'share-results': {
    Icon: Link2,
    label: 'Share results',
    definition: 'Copies a link to the live results page.',
  },
  'share-feedback': {
    Icon: ShareIcon,
    label: 'Share for feedback',
    definition: 'Shares this note with teammates and asks them for feedback.',
  },
  send: {
    Icon: SendIcon,
    label: 'Send',
    definition: 'Sends it to named recipients by email.',
  },
  'post-update': {
    Icon: Megaphone,
    label: 'Post update',
    definition: "Opens the update composer targeted at this report's page.",
  },
  'copy-link': {
    Icon: DuplicateIcon,
    label: 'Copy link',
    definition: 'Copies this row’s personal link to your clipboard.',
  },
  'revoke-link': {
    Icon: Ban,
    label: 'Revoke link',
    definition: 'Invalidates the personal link so it can no longer be opened.',
  },

  // --- Running & output ---
  run: {
    Icon: RunIcon,
    label: 'Run',
    definition: 'Runs the report and shows the output.',
  },
  launch: {
    Icon: RunIcon,
    label: 'Launch',
    definition: 'Makes it live and visible to its audience.',
  },
  'test-link': {
    Icon: FlaskConical,
    label: 'Test link',
    definition: 'Gets a private test link that records no real responses.',
  },
  embed: {
    Icon: EmbedIcon,
    label: 'Embed',
    definition: 'Shows the code snippet for embedding it on another website.',
  },
  download: {
    Icon: DownloadIcon,
    label: 'Export',
    definition: 'Downloads it as a file (CSV, PDF, or Excel, depending on the report).',
  },
  analytics: {
    Icon: AnalyticsIcon,
    label: 'View results',
    definition: 'Opens the response analytics for this item.',
  },
  // CheckCircle, matching the glyph OutreachTab's close button actually
  // renders — a status-flavored choice for an action, but the legend's job
  // is to draw the real glyph.
  'close-outreach': {
    Icon: SuccessIcon,
    label: 'Close',
    definition: 'Closes it to new responses; existing results are kept.',
  },

  // --- Workspace tracking ---
  track: {
    Icon: TrackIcon,
    label: 'Track',
    definition: 'Adds it to one of your workspaces so your team can follow it.',
  },
  priority: {
    Icon: Star,
    label: 'Priority',
    definition: 'Marks it as a priority for this workspace (click again to clear).',
  },
  hot: {
    Icon: Flame,
    label: 'Hot',
    definition: 'Marks it as a hot measure for this workspace (click again to clear).',
  },

  // --- Domain-specific ---
  manage: {
    Icon: SettingsIcon,
    label: 'Manage',
    definition: 'Opens the settings and membership for this workspace.',
  },
  reply: {
    Icon: MessageSquare,
    label: 'Reply',
    definition: 'Writes a threaded reply to this note.',
  },
  votes: {
    Icon: Vote,
    label: 'View votes',
    definition: 'Shows the recorded votes on this bill.',
  },
  'vote-predictions': {
    Icon: Vote,
    label: 'Vote predictions',
    definition: 'Opens the vote predictions for this bill.',
  },
  'lobbyist-filings': {
    Icon: PeopleIcon,
    label: 'Lobbyist filings',
    definition: 'Shows the lobbyists who filed on this bill.',
  },
  'add-to-calendar': {
    Icon: CalendarPlus,
    label: 'Add to calendar',
    definition: 'Adds this event to your Outlook, Google, or Apple calendar.',
  },
} as const satisfies Record<string, ActionIconGlossaryEntry>;

export type ActionIconKey = keyof typeof ACTION_ICON_GLOSSARY;
